import type {
  TaskItem,
  TeamRole,
  TeamAssignedTaskItem,
  TeamTaskBucket,
  TeamTaskItem,
  TeamTaskMemberSummary,
  TeamTaskProjectSummary,
  TeamTaskSource,
  TodoAppFrom,
} from '@demind-inc/core';

import {
  TeamAssignedTasksItem as ApiTeamAssignedTasksItem,
  TeamTasksItem as ApiTeamTasksItem,
} from '../api';

export type { TeamAssignedTaskItem, TeamTaskBucket, TeamTaskItem } from '@demind-inc/core';

export interface TeamTasksResult {
  date: string;
  counts: {
    all: number;
    overdue: number;
    today: number;
    upcoming: number;
    done: number;
  };
  tasks: TeamTaskItem[];
}

export const TEAM_TASKS_QUERY_KEY = ['lifestack.team.tasks'];
export const TEAM_SHARED_TASKS_QUERY_KEY = [...TEAM_TASKS_QUERY_KEY, 'shared'];
export const TEAM_BOARD_TASKS_QUERY_KEY = [...TEAM_TASKS_QUERY_KEY, 'board'];
export const TEAM_ASSIGNED_TASKS_QUERY_KEY = ['lifestack.team.assignedTasks'];

export type AssignedTeamTaskMeta = Omit<TeamAssignedTaskItem, 'task'>;

export type AssignedTeamTask = TaskItem & {
  isAssignedTeamTask: true;
  assignedTeamTask: AssignedTeamTaskMeta;
};

export interface AssignedTeamTasksByDate {
  date: string;
  tasks: AssignedTeamTask[];
  updatedAt?: string;
}

export interface AssignedTeamTasksRangeResult {
  startDate?: string;
  endDate?: string;
  tasksByDate: AssignedTeamTasksByDate[];
  overdueTasks: AssignedTeamTask[];
  teamMembersByTeamId: Record<string, TeamTaskMemberSummary[]>;
}

type ApiTeamTaskItem = ApiTeamTasksItem | ApiTeamAssignedTasksItem;

const isTeamTaskBucket = (bucket: string): bucket is TeamTaskBucket => {
  return bucket === 'overdue' || bucket === 'today' || bucket === 'upcoming' || bucket === 'done';
};

const isTeamTaskSource = (source: string): source is TeamTaskSource => {
  return source === 'shared-personal-board' || source === 'team-board';
};

const isTeamRole = (role?: string): role is TeamRole => {
  return role === 'owner' || role === 'admin' || role === 'member';
};

const isTodoAppFrom = (appFrom?: string): appFrom is TodoAppFrom => {
  return (
    appFrom === 'lifestack' ||
    appFrom === 'trello' ||
    appFrom === 'todoist' ||
    appFrom === 'ticktick' ||
    appFrom === 'clickup' ||
    appFrom === 'notion' ||
    appFrom === 'github' ||
    appFrom === 'google-tasks' ||
    appFrom === 'apple-reminders'
  );
};

const normalizeTeamTaskMember = (member: ApiTeamTaskItem['owner']): TeamTaskMemberSummary => {
  return {
    userId: member.userId,
    teamMemberId: member.teamMemberId,
    name: member.name,
    photoUrl: member.photoUrl,
    role: isTeamRole(member.role) ? member.role : undefined,
  };
};

const normalizeTeamTaskProject = (project: ApiTeamTaskItem['project']): TeamTaskProjectSummary => {
  return {
    boardId: project.boardId,
    name: project.name,
    appFrom: isTodoAppFrom(project.appFrom) ? project.appFrom : undefined,
    teamId: project.teamId,
  };
};

export const isAssignedTeamTask = (task?: TaskItem | null): task is AssignedTeamTask => {
  return !!task && (task as Partial<AssignedTeamTask>).isAssignedTeamTask === true;
};

export const normalizeTeamTaskItem = (taskItem: ApiTeamTaskItem): TeamTaskItem | null => {
  if (!isTeamTaskBucket(taskItem.bucket) || !isTeamTaskSource(taskItem.source)) {
    return null;
  }

  return {
    source: taskItem.source,
    bucket: taskItem.bucket,
    isOwnTask: taskItem.isOwnTask,
    isMeAssigned: taskItem.isMeAssigned,
    owner: normalizeTeamTaskMember(taskItem.owner),
    assignee: taskItem.assignee ? normalizeTeamTaskMember(taskItem.assignee) : null,
    project: normalizeTeamTaskProject(taskItem.project),
    permissions: taskItem.permissions,
    task: taskItem.task as TaskItem,
  };
};

export const buildAssignedTeamTask = (
  taskItem: ApiTeamAssignedTasksItem
): AssignedTeamTask | null => {
  const normalizedTaskItem = normalizeTeamTaskItem(taskItem);
  if (!normalizedTaskItem) {
    return null;
  }

  const { task, ...teamTaskItem } = normalizedTaskItem;
  const assignedTeamTask: AssignedTeamTaskMeta = {
    ...teamTaskItem,
    team: taskItem.team,
  };
  const taskId = task.taskId;

  if (!taskId) {
    return null;
  }

  return {
    ...task,
    taskId,
    boardId: task.boardId || normalizedTaskItem.project.boardId,
    appFrom: task.appFrom || normalizedTaskItem.project.appFrom || 'lifestack',
    isAssignedTeamTask: true,
    assignedTeamTask,
  } as AssignedTeamTask;
};
